import {useEffect, useMemo, useState} from 'react';
import * as css from './SeatmapManage.css';
import SeatCanvas from './SeatCanvas';
import {getSeatmapManage, setSeatmapSeatTickets, removeSeatmapSeatTicket, setSeatmapSeatState,
    createSeatmapPartner, reserveSeatmapSeats, unreserveSeatmapSeats} from '../../../redux/event/eventActions';
import {bounceMessage} from '../../../layout/userMessages/UserMessage';

// Native seat-map MANAGER for NEW-format events. Renders the smap projection
// directly (no studio, no legacy iframe): select seats (click / box-drag / all),
// then assign & replace ticket types, add multi-ticket, hide, and block/free.
// Reservations (שיריונים) land in the next phase.
const STATE_LEGEND = [
    {key: 'free', label: 'פנוי', color: '#93c5fd'},
    {key: 'blocked', label: 'חסום', color: '#9ca3af'},
    {key: 'hidden', label: 'מוסתר', color: '#e5e7eb'},
    {key: 'reserved', label: 'משוריין', color: '#f59e0b'},
    {key: 'sold', label: 'נמכר', color: '#111827'}
];

function SeatmapManage({eid}) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [mode, setMode] = useState('select');
    const [version, setVersion] = useState(0);
    const [busy, setBusy] = useState(false);
    const [highlightTid, setHighlightTid] = useState(null);
    const [hoverArea, setHoverArea] = useState(null);
    const [assignTid, setAssignTid] = useState('');
    const [reserveSmpid, setReserveSmpid] = useState('');
    const [newPartner, setNewPartner] = useState(null); // null=closed, ''=open empty

    const load = () => getSeatmapManage(eid)
        .then((d) => { setData(d); setErr(null); })
        .catch(() => setErr('load'));
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [eid]);

    const seats = data?.seats || [];
    const tickets = useMemo(() => (data?.tickets || []).filter((t) => t.active !== 0), [data]);
    const selCodes = () => Array.from(selected);

    // per-ticket and per-state counts across the whole map
    const counts = useMemo(() => {
        const t = {}, st = {free: 0, blocked: 0, hidden: 0, reserved: 0, sold: 0}, partner = {};
        for(const s of seats) {
            st[s.state] = (st[s.state] || 0) + 1;
            for(const tid of s.tids || []) t[tid] = (t[tid] || 0) + 1;
            if(s.smpid) partner[s.smpid] = (partner[s.smpid] || 0) + 1;
        }
        return {byTid: t, byState: st, byPartner: partner};
    }, [seats]);

    const selectedSeats = useMemo(() => seats.filter((s) => selected.has(s.code)), [seats, selected]);
    // gush-hover highlight takes priority over the ticket highlight
    const highlightCodes = useMemo(() => {
        if(hoverArea != null) return seats.filter((s) => s.smaid === hoverArea).map((s) => s.code);
        if(highlightTid != null) return seats.filter((s) => (s.tids || []).includes(highlightTid)).map((s) => s.code);
        return null;
    }, [seats, hoverArea, highlightTid]);

    const refetch = async () => { await load(); setVersion((v) => v + 1); };

    const run = async (fn, okMsg) => {
        if(!selected.size) return bounceMessage('בחרו מושבים קודם', 'attention');
        setBusy(true);
        try { await fn(); await refetch(); if(okMsg) bounceMessage(okMsg, 'success'); }
        catch (e) {
            const msg = e?.response?.data?.errorMessage || e?.message;
            bounceMessage(msg && /last ticket|last one/i.test(String(msg)) ? 'אי אפשר להסיר את הכרטיס האחרון ממושב' : 'הפעולה נכשלה', 'attention');
        }
        finally { setBusy(false); }
    };

    // select every seat of a gush (section) at once
    const selectArea = (smaid, additive) => {
        const codes = seats.filter((s) => s.smaid === smaid).map((s) => s.code);
        setSelected((prev) => {
            const next = additive ? new Set(prev) : new Set();
            codes.forEach((c) => next.add(c));
            return next;
        });
    };

    const doAssign = (act) => {
        const tid = Number(assignTid);
        if(!tid) return bounceMessage('בחרו סוג כרטיס', 'attention');
        return run(() => setSeatmapSeatTickets(selCodes(), [tid], act),
            act === 'replace' ? 'הכרטיס שויך למושבים' : 'הכרטיס נוסף למושבים');
    };
    const doRemove = (tid) => run(() => removeSeatmapSeatTicket(selCodes(), tid), 'הכרטיס הוסר מהמושבים');
    const doState = (state, msg) => run(() => setSeatmapSeatState(selCodes(), state), msg);
    const partners = data?.partners || [];
    const doReserve = () => {
        const smpid = Number(reserveSmpid);
        if(!smpid) return bounceMessage('בחרו שותף לשריון', 'attention');
        return run(() => reserveSeatmapSeats(selCodes(), smpid), 'המושבים שוריינו');
    };
    const doUnreserve = () => run(() => unreserveSeatmapSeats(selCodes()), 'השריון בוטל');
    const doCreatePartner = async () => {
        const title = (newPartner || '').trim();
        if(!title) return bounceMessage('שם שותף חסר', 'attention');
        setBusy(true);
        try {
            const p = await createSeatmapPartner({title});
            await load();
            if(p?.smpid) setReserveSmpid(String(p.smpid));
            setNewPartner(null);
            bounceMessage('השותף נוצר', 'success');
        } catch { bounceMessage('יצירת השותף נכשלה', 'attention'); }
        finally { setBusy(false); }
    };

    if(err) return <css.wrap><css.empty>לא ניתן לטעון את מפת המושבים.</css.empty></css.wrap>;
    if(!data) return <css.wrap><css.empty>טוען מפת מושבים…</css.empty></css.wrap>;
    if(!seats.length) return <css.wrap><css.empty>למפה זו אין מושבים.</css.empty></css.wrap>;

    // tickets present on the current selection (for the remove menu)
    const selTids = new Set();
    selectedSeats.forEach((s) => (s.tids || []).forEach((t) => selTids.add(t)));

    return (
        <css.wrap>
            <css.toolbar>
                <css.group>
                    <css.tBtn className={mode === 'select' ? 'on' : ''} onClick={() => setMode('select')}>בחירה</css.tBtn>
                    <css.tBtn className={mode === 'pan' ? 'on' : ''} onClick={() => setMode('pan')}>הזזה</css.tBtn>
                    <css.sep />
                    <css.tBtn onClick={() => setSelected(new Set(seats.map((s) => s.code)))}>בחר הכל</css.tBtn>
                    <css.tBtn onClick={() => setSelected(new Set())}>נקה בחירה</css.tBtn>
                    <css.count>{selected.size ? `נבחרו ${selected.size} מושבים` : 'לחיצה/גרירה לבחירת מושבים · גלגלת לזום · רווח+גרירה להזזה'}</css.count>
                </css.group>
                {!!selected.size &&
                    <css.group>
                        <css.assign>
                            <select value={assignTid} onChange={(e) => setAssignTid(e.target.value)} disabled={busy}>
                                <option value="">סוג כרטיס…</option>
                                {tickets.map((t) => <option key={t.tid} value={t.tid}>{t.title}</option>)}
                            </select>
                            <css.tBtn className="primary" disabled={busy} onClick={() => doAssign('replace')}>שייך</css.tBtn>
                            <css.tBtn disabled={busy} onClick={() => doAssign('add')}>הוסף כרטיס</css.tBtn>
                        </css.assign>
                        <css.sep />
                        <css.tBtn disabled={busy} onClick={() => doState('blocked', 'המושבים נחסמו')}>חסום</css.tBtn>
                        <css.tBtn disabled={busy} onClick={() => doState('hidden', 'המושבים הוסתרו')}>הסתר</css.tBtn>
                        <css.tBtn disabled={busy} onClick={() => doState('free', 'המושבים שוחררו')}>שחרר</css.tBtn>
                        <css.sep />
                        <css.assign>
                            <select value={reserveSmpid} onChange={(e) => setReserveSmpid(e.target.value)} disabled={busy}>
                                <option value="">שותף…</option>
                                {partners.map((p) => <option key={p.smpid} value={p.smpid}>{p.title}</option>)}
                            </select>
                            <css.tBtn disabled={busy} onClick={doReserve}>שריין</css.tBtn>
                            <css.tBtn disabled={busy} onClick={doUnreserve}>בטל שריון</css.tBtn>
                            {newPartner == null
                                ? <css.tBtn disabled={busy} onClick={() => setNewPartner('')}>+ שותף</css.tBtn>
                                : <css.newPartner>
                                    <input autoFocus value={newPartner} placeholder="שם שותף"
                                        onChange={(e) => setNewPartner(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && doCreatePartner()} />
                                    <css.tBtn className="primary" disabled={busy} onClick={doCreatePartner}>צור</css.tBtn>
                                    <css.tBtn disabled={busy} onClick={() => setNewPartner(null)}>ביטול</css.tBtn>
                                </css.newPartner>}
                        </css.assign>
                    </css.group>}
            </css.toolbar>

            <css.body>
                <SeatCanvas seats={seats} bounds={data.bounds} dataVersion={version}
                    selected={selected} setSelected={setSelected} mode={mode} highlightCodes={highlightCodes}
                    areas={data.areas} onSelectArea={selectArea} onHoverArea={setHoverArea} />

                <css.side>
                    <css.sideTitle>{data.title || 'מפת מושבים'} · {seats.length.toLocaleString('he')} מושבים</css.sideTitle>
                    <css.sideSub>כרטיסים</css.sideSub>
                    {!tickets.length && <css.empty>אין כרטיסים פעילים באירוע.</css.empty>}
                    {tickets.map((t) => (
                        <css.ticketRow key={t.tid}
                            className={highlightTid === t.tid ? 'on' : ''}
                            onClick={() => setHighlightTid(highlightTid === t.tid ? null : t.tid)}>
                            <css.dot style={{background: `#${(t.color || '').replace('#', '') || '93c5fd'}`}} />
                            <span className="title">{t.title}</span>
                            <span className="cnt">{counts.byTid[t.tid] || 0}</span>
                            {selTids.has(t.tid) &&
                                <css.rm title="הסר מהמושבים הנבחרים" onClick={(e) => { e.stopPropagation(); doRemove(t.tid); }}>×</css.rm>}
                        </css.ticketRow>
                    ))}
                    {!!partners.length &&
                        <>
                            <css.sideSub>שיריונים</css.sideSub>
                            {partners.map((p) => (
                                <css.legendRow key={p.smpid}><css.dot style={{background: '#f59e0b'}} /><span>{p.title}</span><span className="cnt">{counts.byPartner[p.smpid] || 0}</span></css.legendRow>
                            ))}
                        </>}
                    <css.sideSub>מקרא</css.sideSub>
                    {STATE_LEGEND.map((l) => (
                        <css.legendRow key={l.key}><css.dot style={{background: l.color}} /><span>{l.label}</span><span className="cnt">{counts.byState[l.key] || 0}</span></css.legendRow>
                    ))}
                    <css.sideSub>אזורים (לחצו לבחירת גוש)</css.sideSub>
                    <css.areas>{(data.areas || []).map((a) => (
                        <div key={a.smaid} className="clickable"
                            onMouseEnter={() => setHoverArea(a.smaid)} onMouseLeave={() => setHoverArea(null)}
                            onClick={(e) => selectArea(a.smaid, e.shiftKey)}>{a.title}</div>
                    ))}</css.areas>
                </css.side>
            </css.body>
        </css.wrap>
    );
}

export default SeatmapManage;
